import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetAllBrands } from '../../api/queries/getAllBrands/getAllBrands';
import { BrandDataType } from './BrandsList';
import { useCatalogContext } from '../../state/Catalog';
import { useGetDepartmentQuery } from '@graphql/generated';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import { useTranslation } from '@shared/core';

export type BrandListData = { key: string; brands: BrandDataType[] };
export type CategoryFilterData = { id: string; title: string; locator?: string | null };

const ALPHABET_LIST = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', '0-9'];
const BREADCRUMB_HEIGHT = 56;
export const DEFAULT_FILTER = { id: 'all', title: 'All', locator: 'all' };
const COLLECTIONS_ID = 'e3a0b317-88fa-5d1e-9fb1-7e789d2bf982';

export const useBrandsListController = () => {
  const [selectedItem, setSelectedItem] = useState('A');
  const [hasBrandGroupMounted, setHasBrandGroupMounted] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<CategoryFilterData>(DEFAULT_FILTER);

  const { currentStorefront } = useShopStorefrontContext();
  const { t } = useTranslation('catalogRegistry');
  const seoTrans = t('seo', currentStorefront);

  const category = selectedFilter.id === DEFAULT_FILTER.id ? null : selectedFilter.locator;
  const { brandList, loading } = useGetAllBrands({ category, storefront: currentStorefront });
  const { data } = useGetDepartmentQuery({ variables: { storefront: currentStorefront }, batchMode: 'fast', fetchPolicy: 'cache-first' });
  const categories = data?.productCatalogCategoryTreeRoot.children;

  const isAutoScrollActive = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, Element | null>>({});
  const quickAccessRef = useRef<HTMLDivElement>(null);
  const shopByRef = useRef<HTMLDivElement>(null);

  const { isMobile, selectedCategory } = useCatalogContext();

  // This useEffect is to move the selected quickAccess item to the center of the viewport
  useEffect(() => {
    const cb = () => {
      if (isAutoScrollActive.current) {
        isAutoScrollActive.current = false;
        document.getElementById(`quickAccess-${selectedItem}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    };
    window.addEventListener('scrollend', cb);
    return () => {
      window.removeEventListener('scrollend', cb);
    };
  }, [selectedItem]);

  const formattedBrandList = useMemo(() => {
    if (!brandList) {
      return [];
    }
    const groupedBrands = brandList.reduce((result, brand) => {
      const firstChar = brand.name.charAt(0).toUpperCase();
      const firstLetter = /^[a-zA-Z]$/.test(firstChar) ? firstChar : '0-9';

      const existingGroup = result.find(group => group.key === firstLetter);

      if (existingGroup) {
        existingGroup.brands.push({ id: brand.id, name: brand.name, publicUrlPath: brand.publicUrlPath });
      } else {
        result.push({
          key: firstLetter,
          brands: [{ id: brand.id, name: brand.name, publicUrlPath: brand.publicUrlPath }],
        });
      }

      return result;
    }, [] as BrandListData[]);

    // Sort each group by brand name
    groupedBrands.forEach(group => {
      group.brands.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Sort the groups by their keys (letters) in ascending order
    groupedBrands.sort((a, b) => {
      if (a.key === '0-9') return 1; // Move '0-9' to the end
      if (b.key === '0-9') return -1; // Move '0-9' to the end
      return a.key.localeCompare(b.key);
    });
    return groupedBrands;
  }, [brandList]);

  useEffect(() => {
    if (formattedBrandList.length > 0) {
      isAutoScrollActive.current = true;
      setSelectedItem(formattedBrandList[0].key);
      document.getElementById(`quickAccess-${formattedBrandList[0].key}`)?.scrollIntoView({
        inline: 'center',
        block: 'nearest',
      });
    } else {
      setSelectedItem('');
    }
  }, [formattedBrandList]);

  const formattedCategories = useMemo(() => {
    if (!categories?.length) {
      return [];
    }
    return categories.filter(item => item.id !== COLLECTIONS_ID);
  }, [categories]);

  /** Here an Intersection observer is registered for automatically swicth the selected item
   in the quickAccess when the intersection point is reached. 
   Dependency: Added a  hasBrandGroupMounted dependency so that the registration of the observer will happen
               only after the BrandGroup compnent is mounted **/
  useEffect(() => {
    const headerHeight = headerRef.current?.getBoundingClientRect().height || 0;
    const height = window.innerHeight;
    const headerAndBreadcrumbsHeight = headerHeight + BREADCRUMB_HEIGHT;
    const margin = `24px 0px -${height - (headerAndBreadcrumbsHeight >= height ? 0 : headerAndBreadcrumbsHeight) + 10}px 0px`;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.intersectionRatio > 0 && !isAutoScrollActive.current) {
            setSelectedItem(entry.target.id);
            document.getElementById(`quickAccess-${entry.target.id}`)?.scrollIntoView({
              inline: 'center',
              block: 'nearest',
            });
          }
        });
      },
      { rootMargin: margin },
    );
    const sections = sectionRefs.current;
    Object.keys(sections).forEach(key => sections[key] && observer.observe(sections[key] as Element));

    return () => {
      Object.keys(sections).forEach(key => sections[key] && observer.unobserve(sections[key] as Element));
    };
  }, [hasBrandGroupMounted, brandList]);

  const quickAccessItems = useMemo(
    () =>
      ALPHABET_LIST.map(item => ({
        name: item,
        disabled: !formattedBrandList.find(group => group.key === item),
      })),
    [formattedBrandList],
  );

  const handleSelect = (item: string) => {
    isAutoScrollActive.current = true;
    setSelectedItem(item);
    const sectionRef = sectionRefs.current[item];
    const headerHeight = headerRef.current?.getBoundingClientRect().height || 0;

    // For showing the selected items group in the viewport
    if (sectionRef) {
      window.scrollTo({
        behavior: 'smooth',
        top: sectionRef.getBoundingClientRect().top + window.pageYOffset - (headerHeight + BREADCRUMB_HEIGHT),
      });
    }
  };

  const onFilterSelect = (selectedItem: CategoryFilterData, disableClear: boolean = false) => {
    const headerHeight = headerRef.current?.getBoundingClientRect().height || 0;

    if (disableClear) {
      setSelectedFilter(selectedItem);
      window.scrollTo({
        top: headerHeight + 200,
        behavior: 'smooth',
      });
      return;
    }

    setSelectedFilter(current => {
      const newFilter = current.id === selectedItem.id ? DEFAULT_FILTER : selectedItem;
      window.scrollTo({
        top: headerHeight + 200,
        behavior: 'smooth',
      });
      return newFilter;
    });
  };

  const onClearFilter = () => {
    setSelectedFilter(DEFAULT_FILTER);
    const headerHeight = headerRef.current?.getBoundingClientRect().height || 0;
    window.scrollTo({
      top: headerHeight + 200,
      behavior: 'smooth',
    });
  };

  return {
    seoTrans,
    selectedItem,
    formattedBrandList,
    quickAccessItems,
    sectionRefs,
    headerRef,
    handleSelect,
    isLoadingBrandList: loading,
    isMobile,
    setHasBrandGroupMounted,
    selectedCategory,
    quickAccessRef,
    categories: formattedCategories,
    selectedFilter,
    onFilterSelect,
    onClearFilter,
    shopByRef,
  };
};
